// This is a rather complicated component in the tree.  Why does it exist?
//
// We have several views in the LIHP that host legacy components:
// - Heading
// - Profile widgets
//   - Badges
//   - Projects
//   - Discussions
// - Missions Skills
// - Teachers page
//
// These components all rely on the Profile singleton (profile.js) and they
// were never designed to be used in a Single-Paged Architecture.  Before
// any of these legacy components can be initialized, the profile needs to
// be initialized with the data for the user you are currently looking at.
//
// "But passing a promise to a React component is an antipatern!?"
// That's correct.  We could just pass gate the children in this component and
// only display them once the profile singleton has been initialized.  The
// problem with that is now the child view needs to wait for the profile to
// load and initialize _before_ it can then dynamically load the components to
// show.  By passing a promise, the child views can load these resources in
// parallel.  It is a performance enhancement.
//
// Whenever we re-write or deprecate the dependant components noted above, we
// can get rid of this ugly abstraction.
import * as React from "react";

import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";

import {
    initializeProfile,
    kaidIsDefault,
    type Profile,
} from "./profile-util.ts";

type OwnProps = {
    kaid: string;
    children: (profile: Promise<Profile>) => React.ReactNode;
};

type Props = OwnProps & WithApolloProps;

class ProfileProvider extends React.Component<Props> {
    render(): React.ReactNode {
        const {kaid, children, gqlClient} = this.props;

        if (kaidIsDefault(kaid)) {
            return null;
        }

        const profilePromise = initializeProfile(gqlClient, kaid);

        return children(profilePromise);
    }
}

type ExportProps = WithoutApollo<
    JSX.LibraryManagedAttributes<
        typeof ProfileProvider,
        React.ComponentProps<typeof ProfileProvider>
    >
>;

export default withApollo(ProfileProvider) as React.ComponentType<ExportProps>;
